/**
 * Page Tour — lightweight guided highlight tour.
 * Spotlights each target element with a pulsing orange glow; no overlay.
 */

const TOURS = {
  'progress': [
    {
      title: 'Progress',
      content:
        'This page shows how your <em>actual</em> spending compares to your budget plan. ' +
        'It pulls from the expenses you\'ve logged in the Expense Journal and measures ' +
        'them against the amounts you set in your Budget.',
      target: null,
    },
    {
      title: 'Budget vs Actual Chart',
      content:
        'Each bar represents a budget category. ' +
        '<strong>Spent</strong> (orange) is what you\'ve logged so far. ' +
        '<strong>Scheduled</strong> (blue) is upcoming recurring expenses. ' +
        '<strong>Remaining</strong> is what\'s left. Bars over budget are red.<br><br>' +
        'Click the <strong>▾ dropdown</strong> on any category bar to expand it and see ' +
        'individual line items. Each line item shows vertical markers at key spending ' +
        'points based on its frequency — weekly, bi-weekly, and so on.',
      target: '.progress-left-section .visualization-box',
      scrollBlock: 'start',
    },
    {
      title: 'Key Metrics',
      content:
        '<strong>Total Spent</strong> — everything logged in the Expense Journal this month.<br><br>' +
        '<strong>Daily Pace</strong> — your average daily spend over the last 30 days, useful for projecting where you\'ll land by month-end.<br><br>' +
        '<strong>Planned Spend</strong> — recurring expenses scheduled in the next 7 days.',
      target: '.metrics-grid',
    },
    {
      title: 'Historical Trends',
      content:
        'See how your total spending has changed over the past 3, 6, or 12 months. ' +
        'Use this to spot patterns — seasonal spikes, gradual creep, or genuine improvement.',
      target: '.historical-trends-container',
      scrollBlock: 'start',
    },
    {
      title: 'Spending Comparison',
      content:
        'Compares this month\'s spending by category against last month or the same month last year. ' +
        'Green means you\'re spending less; red means more. ' +
        'A quick way to see where habits are shifting.',
      target: '.month-over-month-container',
      scrollBlock: 'start',
    },
  ],

  'budget': [
    {
      title: 'Your Budget',
      content:
        'This is your financial plan — income on the left, expenses in the middle, ' +
        'and a summary on the right. Everything here is a <em>plan</em>; head to the ' +
        '<strong>Expense Journal</strong> to record actual spending against it.',
      target: null,
    },
    {
      title: 'Budget Name & Actions',
      content:
        'Click the budget name to rename it. Use the buttons to <strong>Save</strong> your ' +
        'changes, set a <strong>Default</strong> budget that loads on startup, ' +
        'create a new budget with <strong>Add</strong>, or switch between saved budgets ' +
        'with <strong>Budgets</strong>.',
      target: '.version-controls',
    },
    {
      title: 'Income',
      content:
        'List every income source here — salary, freelance, rental income, etc. ' +
        'Click a label to rename it. Use <strong>+ Add Income Source</strong> to add more rows. ' +
        'Drag the ⋮⋮ handle to reorder.',
      target: '.section.income',
      scrollBlock: 'start',
    },
    {
      title: 'Expense Categories & Line Items',
      content:
        'Expenses are grouped into <strong>categories</strong> (e.g., Housing) with ' +
        '<strong>line items</strong> underneath (e.g., Rent, Electricity). ' +
        'Click any label to rename it. Drag categories or line items to reorder. ' +
        'Use <strong>+ Add Item</strong> to add a line, or <strong>+ Add New Category</strong> ' +
        'to create a new group.',
      target: '.section.expenses',
      scrollBlock: 'start',
    },
    {
      title: 'Line Item Options',
      content:
        '<strong>Frequency</strong> — set whether an amount is monthly, weekly, annual, etc. ' +
        'Budgley normalises everything to a monthly figure automatically.<br><br>' +
        '<strong>$ badge</strong> — mark as a savings item.<br>' +
        '<strong>🧾 badge</strong> — mark as tax deductible.<br>' +
        '<strong>🔄 badge</strong> — set up as a recurring expense that gets logged automatically.',
      target: '.section.expenses .input-options',
    },
    {
      title: 'Category Targets',
      content:
        'Tap the <strong>target icon</strong> (⊙) next to a category subtotal to set a ' +
        'spending target — either a fixed dollar amount or a percentage of income. ' +
        'Once set, the category shows how much is planned vs. your target.',
      target: '.category-subtotal',
    },
    {
      title: 'Summary',
      content:
        'The summary shows your <strong>Net Amount</strong> (income minus expenses) and ' +
        '<strong>Savings Rate</strong> at a glance. The donut chart breaks down spending ' +
        'by category. A positive net means you\'re planning to save; negative means ' +
        'your expenses exceed your income — time to adjust.',
      target: '.section.summary',
      scrollBlock: 'start',
    },
  ],

  'expense-journal': [
    {
      title: 'Other apps sync your bank. Budgley syncs your brain.',
      content:
        'Most budgeting apps connect to your bank and show you what already happened. ' +
        'Budgley is different on purpose. Every time you log an expense, you pause and ask: ' +
        '<em>How much is left? Am I on track?</em> That 5-second check-in is the moment that ' +
        'actually changes how you spend. We call it the <strong>Journal Moment</strong> — and ' +
        "it's why Budgley works when other apps just\u2026 collect dust." +
        '<p class="tour-footer">Your bank credentials stay with your bank. Your awareness stays with you.</p>',
      target: null,
    },
    {
      title: 'Budget Progress',
      content:
        'This bar shows your month at a glance. ' +
        '<strong>Solid orange</strong> is money already spent; <strong>light blue</strong> is upcoming ' +
        'scheduled expenses. The <strong>Remaining</strong> amount updates in real time. ' +
        "Hover the bar to see a breakdown of what's pending.",
      target: '.budget-progress-section',
    },
    {
      title: 'Pick a Category & Line Item',
      content:
        'Start every entry by choosing the <strong>budget category</strong> (e.g., Food) ' +
        'then the <strong>line item</strong> (e.g., Groceries). ' +
        "Can't find one? Choose <strong>Unplanned Expense</strong> — it still counts toward your totals.",
      target: '.expense-entry-section .form-row',
    },
    {
      title: 'Enter the Amount',
      content:
        'Type a dollar amount, or use <strong>math expressions</strong> directly — ' +
        'e.g. <code>12.50 + 8</code> or <code>3 * 4.99</code>. ' +
        'The quick-tap buttons ($5 / $10 / $20 / $50) fill the field instantly.',
      target: '.amount-group',
    },
    {
      title: 'Affordability Indicator',
      content:
        'As you type, this live indicator shows how the expense fits your budget: ' +
        '<br>✅ <strong>You\'re on track</strong> — plenty left' +
        '<br>⚠️ <strong>Getting close</strong> — less than 20% remaining' +
        '<br>❌ <strong>Over budget</strong> — exceeds what\'s left in this line',
      target: '.affordability-indicator',
    },
    {
      title: 'Tax Deductible & Returns',
      content:
        '<strong>Tax Deductible</strong> — mark expenses that may qualify as a write-off. ' +
        "They're flagged in your history for easy reference at tax time.<br><br>" +
        '<strong>Return</strong> — logging a refund? Check this to record money coming back in; ' +
        'it reduces your running total for that category.',
      target: '.form-options',
    },
    {
      title: 'Journal Entries',
      content:
        'Every logged expense appears here. Use the <strong>date range</strong> to filter by period. ' +
        'Tap the <strong>pencil</strong> to edit an entry, or <strong>delete</strong> one logged by mistake.',
      target: '.expense-history-section',
    },
    {
      title: 'Check-In',
      content:
        'At month-end, compare your journal to your bank statement to catch anything you forgot to log. ' +
        "It's the closing ritual of the Journal Moment — making sure nothing slipped through.",
      target: '.true-up-trigger-btn',
    },
  ],
};

// ── State ──────────────────────────────────────────────────────────────────────

let activeTour = null;
let activeStep = 0;
let cardEl = null;
let spotlitEl = null;

// ── DOM bootstrap ──────────────────────────────────────────────────────────────

function ensureTourDOM() {
  if (cardEl) return;
  cardEl = document.createElement('div');
  cardEl.className = 'tour-card';
  document.body.appendChild(cardEl);
}

// ── Public API ─────────────────────────────────────────────────────────────────

export function startPageTour(name) {
  const steps = TOURS[name];
  if (!steps) return;
  ensureTourDOM();
  activeTour = steps;
  activeStep = 0;
  document.addEventListener('keydown', handleKey);
  renderStep();
}

function endTour() {
  if (!activeTour) return;
  activeTour = null;
  document.removeEventListener('keydown', handleKey);
  cardEl.classList.remove('tour-card--visible');
  clearSpotlight();
}

// ── Rendering ──────────────────────────────────────────────────────────────────

function renderStep() {
  const step = activeTour[activeStep];
  const total = activeTour.length;
  const isFirst = activeStep === 0;
  const isLast = activeStep === total - 1;

  clearSpotlight();

  spotlitEl = step.target ? document.querySelector(step.target) : null;
  if (spotlitEl) {
    spotlitEl.classList.add('tour-spotlight');
    spotlitEl.scrollIntoView({ behavior: 'smooth', block: step.scrollBlock || 'center' });
  }

  cardEl.innerHTML = `
    <div class="tour-card-header">
      <span class="tour-step-count">${activeStep + 1} of ${total}</span>
      <button class="tour-close" aria-label="Close tour">×</button>
    </div>
    <h4 class="tour-title">${step.title}</h4>
    <div class="tour-body">${step.content}</div>
    <div class="tour-nav">
      <button class="tour-btn tour-btn--secondary" id="tour-prev" ${isFirst ? 'disabled' : ''}>← Back</button>
      <button class="tour-btn tour-btn--primary" id="tour-next">${isLast ? 'Done' : 'Next →'}</button>
    </div>
  `;

  cardEl.querySelector('.tour-close').addEventListener('click', endTour);
  cardEl.querySelector('#tour-prev').addEventListener('click', () => {
    if (activeStep > 0) { activeStep--; renderStep(); }
  });
  cardEl.querySelector('#tour-next').addEventListener('click', () => {
    if (isLast) { endTour(); } else { activeStep++; renderStep(); }
  });

  cardEl.classList.add('tour-card--visible');

  // Position after scroll settles
  setTimeout(() => positionCard(spotlitEl), 320);
}

function positionCard(targetEl) {
  cardEl.style.top = '';
  cardEl.style.left = '';
  cardEl.style.transform = '';

  if (!targetEl) {
    cardEl.style.top = '50%';
    cardEl.style.left = '50%';
    cardEl.style.transform = 'translate(-50%, -50%)';
    return;
  }

  const rect = targetEl.getBoundingClientRect();
  const cardW = cardEl.offsetWidth || 340;
  const cardH = cardEl.offsetHeight || 220;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 16;

  let top, left;

  if (rect.bottom + gap + cardH < vh) {
    top = rect.bottom + gap;
  } else if (rect.top - gap - cardH > 0) {
    top = rect.top - gap - cardH;
  } else {
    top = Math.max(gap, Math.min(rect.top, vh - cardH - gap));
  }

  left = Math.max(gap, Math.min(rect.left, vw - cardW - gap));

  cardEl.style.top = `${top}px`;
  cardEl.style.left = `${left}px`;
}

function clearSpotlight() {
  if (spotlitEl) {
    spotlitEl.classList.remove('tour-spotlight');
    spotlitEl = null;
  }
}

function handleKey(e) {
  if (e.key === 'Escape') endTour();
  if (e.key === 'ArrowRight' && activeStep < activeTour.length - 1) { activeStep++; renderStep(); }
  if (e.key === 'ArrowLeft' && activeStep > 0) { activeStep--; renderStep(); }
}
